import {
  createUuid, type Uuid,
} from '~/_shared/utils/createUuid';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import type { BoundaryGroupDemographic } from '~/store/boundaryGroups/boundaryGroups.repository';

export type ReportTemplateItem = ReportTemplate['items'][number];
type ReportTemplateTableItem = Extract<ReportTemplateItem, { type: 'table' }>;
export type ReportTemplateTableRow = ReportTemplateTableItem['content'][number];
export type ReportTemplateDataRow = Extract<ReportTemplateTableRow, { type: 'data-row' }>;
type BuilderReportTemplateNonTableItem = Exclude<ReportTemplateItem, ReportTemplateTableItem> & Readonly<{ uuid: Uuid }>;
export type BuilderReportTemplateTableRow = ReportTemplateTableRow & Readonly<{ uuid: Uuid }>;
export type BuilderReportTemplateTableItem = Omit<ReportTemplateTableItem, 'content'> & Readonly<{
  uuid: Uuid;
  content: ReadonlyArray<BuilderReportTemplateTableRow>;
}>;
export type BuilderReportTemplateItem = BuilderReportTemplateNonTableItem | BuilderReportTemplateTableItem;
export type BuilderReportTemplate = Omit<ReportTemplate, 'items'> & Readonly<{
  items: ReadonlyArray<BuilderReportTemplateItem>;
}>;

export type DemographicOption = Readonly<{
  id: string;
  label: string;
  category: string;
  region: string;
}>;

export const createBlankReportTemplate = (): ReportTemplate => ({
  id: '<FILL_ME_IN>',
  varlistId: '<FILL_ME_IN>',
  name: 'New Report Template',
  items: [
    { type: 'map' },
    {
      type: 'table',
      title: 'Demographics',
      content: [
        { type: 'title-row', content: 'Population' },
        { type: 'data-row', id: 'POPCY' },
      ],
    },
  ],
});

export const createTableRow = (type: ReportTemplateTableRow['type']): BuilderReportTemplateTableRow => {
  switch (type) {
    case 'data-row':
      return { type: 'data-row', id: '', uuid: createUuid() };
    case 'title-row':
      return { type: 'title-row', content: '', uuid: createUuid() };
    case 'empty-row':
      return { type: 'empty-row', uuid: createUuid() };
    default:
      throw new Error(`Unsupported table row type: ${type satisfies never}`);
  }
};

export const createTemplateItem = (type: ReportTemplateItem['type']): BuilderReportTemplateItem => {
  switch (type) {
    case 'map':
      return { type: 'map', uuid: createUuid() };
    case 'line-break':
      return { type: 'line-break', uuid: createUuid() };
    case 'page-break':
      return { type: 'page-break', uuid: createUuid() };
    case 'title':
      return { type: 'title', content: '', uuid: createUuid() };
    case 'table':
      return { type: 'table', title: '', content: [], uuid: createUuid() };
    case 'notes-and-references':
      return { type: 'notes-and-references', uuid: createUuid() };
    default:
      throw new Error(`Unsupported template item type: ${type satisfies never}`);
  }
};

const addUuidToReportTemplateTableRow = (row: ReportTemplateTableRow): BuilderReportTemplateTableRow => ({
  ...row,
  uuid: createUuid(),
});

const addUuidToReportTemplateItem = (item: ReportTemplateItem): BuilderReportTemplateItem => item.type === 'table'
  ? {
    ...item,
    uuid: createUuid(),
    content: item.content.map(addUuidToReportTemplateTableRow),
  }
  : {
    ...item,
    uuid: createUuid(),
  };

export const createBuilderReportTemplate = (template: ReportTemplate): BuilderReportTemplate => ({
  ...template,
  items: template.items.map(addUuidToReportTemplateItem),
});

const removeUuidFromBuilderReportTemplateTableRow = (row: BuilderReportTemplateTableRow): ReportTemplateTableRow => {
  const { uuid: _uuid, ...reportRow } = row;

  return reportRow;
};

const removeUuidFromBuilderReportTemplateItem = (item: BuilderReportTemplateItem): ReportTemplateItem => {
  if (item.type === 'table') {
    const { content, uuid: _uuid, ...reportItem } = item;

    return { ...reportItem, content: content.map(removeUuidFromBuilderReportTemplateTableRow) };
  }

  const { uuid: _uuid, ...reportItem } = item;

  return reportItem;
};

export const createReportTemplate = (template: BuilderReportTemplate): ReportTemplate => ({
  ...template,
  items: template.items.map(removeUuidFromBuilderReportTemplateItem),
});

export const moveArrayItem = <T>(items: ReadonlyArray<T>, fromIndex: number, toIndex: number): ReadonlyArray<T> => {
  if (toIndex < 0 || toIndex >= items.length || fromIndex === toIndex) {
    return items;
  }

  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  if (item !== undefined) {
    nextItems.splice(toIndex, 0, item);
  }

  return nextItems;
};

export const flattenDemographicOptions = (
  regions: ReadonlyArray<{ id: string; demographics: readonly BoundaryGroupDemographic[] }>
): ReadonlyArray<DemographicOption> => regions.flatMap(region => region.demographics.map(demographic => ({
  id: demographic.id,
  label: demographic.name,
  category: demographic.category,
  region: region.id,
})));

export const getDemographicLabelMap = (options: ReadonlyArray<DemographicOption>): ReadonlyMap<string, string> => {
  return new Map(options.flatMap(option => [
    [option.id, option.label] as const,
    [`${option.id}%`, `${option.label} %`] as const,
    [`%${option.id}`, `${option.label} %`] as const,
  ]));
};

export const getInvalidDemographicDataRows = (
  template: BuilderReportTemplate,
  options: ReadonlyArray<DemographicOption>
): ReadonlyArray<{ rowUuid: Uuid; id: string; tableIndex: number; tableTitle?: string; rowIndex: number }> => {
  const demographicIds = new Set(options.flatMap(option => [option.id, `${option.id}%`]));
  const isValidId = (id: string) => demographicIds.has(id) || (id.startsWith('%') && demographicIds.has(id.slice(1)));

  return template.items.flatMap((item, itemIndex) => item.type === 'table'
    ? item.content.flatMap((row, rowIndex) => {
      if (row.type !== 'data-row' || row.formula || isValidId(row.id)) {
        return [];
      }

      return [{ rowUuid: row.uuid, id: row.id, tableIndex: itemIndex, tableTitle: item.title, rowIndex }];
    })
    : []);
};

type DemographicOptionMatchScore = Readonly<{
  rank: number;
  length: number;
}>;

const getDemographicOptionFieldMatchScore = (value: string, normalizedSearch: string, rank: number): DemographicOptionMatchScore | null => {
  const normalizedValue = value.toLowerCase();

  if (!normalizedValue.includes(normalizedSearch)) {
    return null;
  }

  const matchingPartLength = normalizedValue
    .split(/[^a-z0-9%]+/)
    .filter(part => part.includes(normalizedSearch))
    .reduce((length, part) => Math.min(length, part.length), normalizedValue.length);

  return {
    rank,
    length: matchingPartLength,
  };
};

const getDemographicOptionMatchScore = (option: DemographicOption, normalizedSearch: string): DemographicOptionMatchScore | null => {
  const fieldScores = [
    getDemographicOptionFieldMatchScore(option.id, normalizedSearch, option.id.toLowerCase().startsWith(normalizedSearch) ? 0 : 1),
    getDemographicOptionFieldMatchScore(option.label, normalizedSearch, option.label.toLowerCase().startsWith(normalizedSearch) ? 2 : 3),
    getDemographicOptionFieldMatchScore(option.category, normalizedSearch, option.category.toLowerCase().startsWith(normalizedSearch) ? 4 : 5),
    getDemographicOptionFieldMatchScore(option.region, normalizedSearch, option.region.toLowerCase().startsWith(normalizedSearch) ? 6 : 7),
  ].filter(score => score !== null);

  return fieldScores.reduce<DemographicOptionMatchScore | null>((bestScore, score) => {
    if (!bestScore || score.rank < bestScore.rank || (score.rank === bestScore.rank && score.length < bestScore.length)) {
      return score;
    }

    return bestScore;
  }, null);
};

export const filterDemographicOptions = (
  options: ReadonlyArray<DemographicOption>,
  search: string
): ReadonlyArray<DemographicOption> => {
  const normalizedSearch = search.replace(/^%/, '').trim().toLowerCase();
  if (!normalizedSearch) {
    return options.slice(0, 100);
  }

  return options
    .map(option => ({
      matchScore: getDemographicOptionMatchScore(option, normalizedSearch),
      option,
    }))
    .filter(({ matchScore }) => matchScore !== null)
    .sort((a, b) => {
      if (!a.matchScore || !b.matchScore) {
        return 0;
      }

      return a.matchScore.rank - b.matchScore.rank || a.matchScore.length - b.matchScore.length || a.option.id.localeCompare(b.option.id);
    })
    .map(({ option }) => option)
    .slice(0, 100);
};

export const getTemplateFileName = (template: ReportTemplate): string => {
  const safeId = template.id.trim().replace(/[^a-z0-9_-]+/gi, '_') || 'report-template';

  return `${safeId}.rt.yml`;
};
